import { formatDateTime, formatBytes, timeAgo, DEFAULT_TZ, SortTh } from '../../utils';
import { Badge } from './Badge';
import { DelegatedButton } from './DelegatedButton';
import { RunProgressLine, RunStatusBadge as SharedRunStatusBadge } from '../RunProgress';

const ANOMALY_FLAG_LABELS = {
  size_drop:        'Caída de tamaño',
  data_added_spike: 'Pico de datos nuevos',
};

function AnomalyBadge({ r }) {
  if (!r.anomaly_score || r.anomaly_score <= 0) return null;
  const severe = r.anomaly_score >= 0.67;
  const flagLabels = (r.anomaly_flags || []).map(f => ANOMALY_FLAG_LABELS[f] || f).join(', ');
  return (
    <span
      title={`Anomalía ${severe ? 'severa' : 'moderada'}: ${flagLabels}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        color: severe ? 'var(--red)' : 'var(--yellow)',
        fontWeight: 600, fontSize: 10,
        background: severe ? 'rgba(220,38,38,0.1)' : 'rgba(245,158,11,0.1)',
        padding: '2px 6px', borderRadius: 3, marginLeft: 6,
        fontFamily: 'var(--mono)', letterSpacing: 1, cursor: 'help',
      }}
    >
      ⚠ ANOMALÍA {(r.anomaly_score * 100).toFixed(0)}%
    </span>
  );
}

// #213 — el motivo del error: title en el badge (cursor 'help', como los otros
// badges con tooltip) y, en mobile y desktop, una línea chica debajo cortada con
// elipsis a dos líneas, completa en el title. Los badges de honeypot y de corte
// por ventana no pasan por acá: ya usan error_msg.
function DefaultRunBadge({ r, isMobile }) {
  const badge = <><Badge s={r.status} /><AnomalyBadge r={r} /></>;
  if (!r.error_msg) return badge;
  return (
    <span title={r.error_msg} style={{ display: 'inline-flex', flexDirection: 'column', alignItems: isMobile ? 'flex-end' : 'flex-start', maxWidth: isMobile ? 180 : 280, cursor: 'help' }}>
      <span>{badge}</span>
      <span title={r.error_msg} style={{ fontSize: 10, color: 'var(--muted)', fontFamily: 'var(--mono)', marginTop: 4, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflowWrap: 'anywhere', cursor: 'help' }}>
        {r.error_msg}
      </span>
    </span>
  );
}

// #213 — el snapshot de este run ya no existe en el repo (retención): sin botón.
function RemovedByRetention({ r, tz }) {
  return (
    <span
      title={`Eliminado por la política de retención el ${formatDateTime(r.snapshot_removed_at, tz)}`}
      style={{ fontSize: 10, padding: '3px 8px', border: '1px solid var(--border)', borderRadius: 3, color: 'var(--muted)', fontFamily: 'var(--mono)', letterSpacing: 1, cursor: 'help' }}
    >
      ELIMINADO POR RETENCIÓN
    </span>
  );
}

function RunStatusBadge({ r, isMobile }) {
  return <SharedRunStatusBadge r={r} isMobile={isMobile} DefaultBadge={DefaultRunBadge} />;
}

export function RunHistoryTab({ sortedRuns, serverTzMap, isMobile, delegations, isClient, canExplore = false, overrideActive, setExplorer, runSortKey, runSortDir, runToggle }) {
  return (
    isMobile ? (
      <div>
        {sortedRuns.length === 0 ? (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', padding: 40, textAlign: 'center', fontFamily: 'var(--mono)', color: 'var(--muted)' }}>Sin historial todavía</div>
        ) : sortedRuns.map((r) => {
          const tz = r.server_timezone || serverTzMap[r.server_id] || DEFAULT_TZ;
          return (
            <div key={r.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', padding: '14px 16px', marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 2 }}>FECHA</div>
              <div style={{ fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--muted)', marginBottom: 10 }}>{formatDateTime(r.started_at, tz)}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 2 }}>JOB / SERVIDOR</div>
              <div style={{ fontSize: 13, marginBottom: 4 }}>{r.job_name}</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10 }}>{r.server}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--accent)', marginRight: 12 }}>{r.size_bytes ? formatBytes(r.size_bytes) : '—'}</span>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>{r.duration_seconds ? r.duration_seconds + 's' : '—'}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {r.job_type === 'integrity_check' && (
                    <span style={{ fontSize: 10, padding: '1px 6px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 3, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>INTEGRITY</span>
                  )}
                  <RunStatusBadge r={r} isMobile={isMobile} />
                </div>
              </div>
              <RunProgressLine r={r} tz={tz} />
              {r.snapshot_id && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                  {r.snapshot_removed_at ? <RemovedByRetention r={r} tz={tz} /> : canExplore && (
                  <DelegatedButton
                    operation="can_explore"
                    delegations={delegations}
                    isClient={isClient}
                    overrideActive={overrideActive}
                    onClick={() => setExplorer(r)}
                    style={{ background: 'transparent', border: `1px solid ${r.status === 'error' ? 'var(--yellow)' : 'var(--green)'}`, color: r.status === 'error' ? 'var(--yellow)' : 'var(--green)', padding: '5px 10px', fontSize: 11, fontFamily: 'var(--mono)' }}
                  >
                    RESTAURAR
                  </DelegatedButton>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    ) : (
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: 'var(--mono)' }}>
          <thead>
            <tr style={{ background: 'var(--bg)' }}>
              {[
                ['Fecha',      'started_at'],
                ['Job',        'job_name'],
                ['Servidor',   'server'],
                ['Duración',   'duration_seconds'],
                ['Procesados', 'size_bytes'],
                ['Añadidos',   'data_added_bytes'],
                ['Estado',     'status'],
              ].map(([label, col]) => (
                <SortTh key={col} label={label} colKey={col} sortKey={runSortKey} sortDir={runSortDir} onToggle={runToggle}
                  style={{ padding: '12px 16px', textAlign: 'left', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', borderBottom: '1px solid var(--border)' }} />
              ))}
              <th style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}></th>
            </tr>
          </thead>
          <tbody>
            {sortedRuns.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>Sin historial todavía</td></tr>
            ) : sortedRuns.map((r) => {
              const tz = r.server_timezone || serverTzMap[r.server_id] || DEFAULT_TZ;
              return (
                <tr key={r.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '14px 16px', color: 'var(--muted)', fontSize: 11 }}
                      title={timeAgo(r.started_at)}>
                    {formatDateTime(r.started_at, tz)}
                  </td>
                  <td style={{ padding: '14px 16px' }}>{r.job_name}</td>
                  <td style={{ padding: '14px 16px', color: 'var(--muted)' }}>{r.server}</td>
                  <td style={{ padding: '14px 16px', color: 'var(--muted)' }}>{r.duration_seconds ? r.duration_seconds+'s' : '—'}</td>
                  <td style={{ padding: '14px 16px', color: 'var(--accent)' }}>{r.size_bytes ? formatBytes(r.size_bytes) : '—'}</td>
                  <td style={{ padding: '14px 16px', color: 'var(--green)' }}>{r.data_added_bytes ? formatBytes(r.data_added_bytes) : '—'}</td>
                  <td style={{ padding: '14px 16px' }}>
                    {r.job_type === 'integrity_check' && (
                      <span style={{ fontSize: 10, padding: '1px 6px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 3, color: 'var(--muted)', fontFamily: 'var(--mono)', marginRight: 6 }}>INTEGRITY</span>
                    )}
                    <RunStatusBadge r={r} isMobile={isMobile} />
                    <RunProgressLine r={r} tz={tz} />
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    {r.snapshot_id && (r.snapshot_removed_at ? <RemovedByRetention r={r} tz={tz} /> : canExplore && (
                      <DelegatedButton
                        operation="can_explore"
                        delegations={delegations}
                        isClient={isClient}
                        overrideActive={overrideActive}
                        onClick={() => setExplorer(r)}
                        style={{ background: 'transparent', border: `1px solid ${r.status === 'error' ? 'var(--yellow)' : 'var(--green)'}`, color: r.status === 'error' ? 'var(--yellow)' : 'var(--green)', padding: '5px 10px', fontSize: 11, fontFamily: 'var(--mono)' }}
                      >
                        RESTAURAR
                      </DelegatedButton>
                    ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )
  );
}
